import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Job } from '@vromo/models';
import Title from '@vromo/ui/components/Title';
import Modal, { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import Button from '@vromo/ui/components/Button';
import { Upload } from '@vromo/models';
import { css } from 'emotion';
import theme from '@vromo/ui/components/theme';
import format from 'date-fns/format';
import sortBy from 'lodash/sortBy';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { useModals } from '@vromo/ui/components/Modals';
import { useToasts } from '@vromo/ui/components/Toasts';
import { action } from 'mobx';
import { RootStore } from 'dashboard/src/stores/RootStore';

interface Props {
    isOpen: boolean;
    selectedJob?: Job;
    onClose: () => void;
}

export const PhotoInspector = observer<{
    selectedPhoto?: Upload;
    selectedJob?: Job;
    removePhoto: (id: number) => void;
}>(function PhotoInspector({ selectedPhoto, selectedJob, removePhoto }) {
    const {
        authStore: { graph, role }
    } = useStore<RootStore>('rootStore');
    const modals = useModals();
    const showToast = useToasts();

    const openPhotoInNewTab = (p: Upload) => {
        if (p.url) {
            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = p.url;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    async function deletePhoto(p: Upload) {
        if (
            selectedJob?.id &&
            p.id &&
            (await modals.confirm('Delete Photo', 'Are you sure you want to delete this photo?'))
        ) {
            graph.root
                .graph()
                .roles(role.id)
                .jobs(selectedJob.id)
                .uploads()
                .delete({ _all_id__in: p.id })
                .then(
                    action(() => {
                        showToast('Photo successfully deleted.', 'info');
                        p.id && removePhoto(p.id);
                    })
                )
                .catch((e) => {
                    console.error(e);
                    showToast('Something went wrong with deleting this photo. Try again.', 'error');
                });
        }
    }

    return (
        <div
            className={css`
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                height: 500px;
                padding: 20px;
                width: 30vw;
                border-left: 1px solid ${theme.lightGrey};

                @media (max-width: 1000px) {
                    flex: 1;
                    height: 300px;
                    padding: 0px;
                    width: 100%;
                    border: none;
                }
            `}>
            {!selectedPhoto ? (
                <p>Select a photo to inspect.</p>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%'
                    }}>
                    <img
                        src={selectedPhoto?.url || ''}
                        className={css`
                            max-height: 80%;
                            max-width: 90%;
                            width: auto;
                            border-radius: 4px;
                            margin: 10px 20px;
                        `}
                    />
                    <h3>
                        Uploaded at{' '}
                        {selectedPhoto.created
                            ? format(selectedPhoto.created, ` hh:mma DD MMM YYYY `)
                            : ' an Unknown Time'}
                    </h3>
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-around'
                        }}>
                        <Button primary small onClick={() => openPhotoInNewTab(selectedPhoto)}>
                            <FontAwesome icon="external-link-alt" style={{ marginRight: 5 }} />
                            Open in New Tab
                        </Button>
                        <Button destructive small onClick={() => deletePhoto(selectedPhoto)}>
                            <FontAwesome icon="trash-alt" style={{ marginRight: 5 }} />
                            Delete
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
});

export const PhotoExplorer = observer<{
    photos?: Array<Upload>;
    selectedPhoto?: Upload;
    selectPhoto: (id: number) => void;
}>(function PhotoExplorer({ photos, selectedPhoto, selectPhoto }) {
    return (
        <div style={{ flex: 1 }}>
            {photos && photos.length > 0 && (
                <div
                    className={css`
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        max-width: 600px;

                        @media (max-width: 1000px) {
                            flex-wrap: nowrap;
                            overflow: scroll;
                        }
                    `}>
                    {sortBy(photos, (p) => p.created).map(({ id, url }) => {
                        const isSelected = id == selectedPhoto?.id;

                        return (
                            <img
                                src={url || ''}
                                key={id}
                                onClick={() => selectPhoto(id!)}
                                className={css`
                                    cursor: pointer;
                                    max-width: 100px;
                                    height: auto;
                                    border-radius: 4px;
                                    margin: 10px 20px;
                                    opacity: ${isSelected ? 1 : 0.3};
                                    box-shadow: ${isSelected
                                        ? `0px 0px 0px 3px ${theme.primary}`
                                        : 'none'};
                                    transition: all 150ms ease-in-out;

                                    &:hover {
                                        opacity: 1;
                                    }
                                `}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
});

export default observer<Props>(function PhotoHistoryModal({ isOpen, selectedJob, onClose }) {
    const [photos, setPhotos] = useState<Array<Upload> | undefined>();
    const [selectedPhoto, setSelectedPhoto] = useState<Upload | undefined>();

    useEffect(() => {
        if (isOpen) {
            setSelectedPhoto(undefined);
            setPhotos(selectedJob?.uploads?.items.filter((it) => it.category === 'photo'));
        }
    }, [isOpen]);

    const selectPhoto = (id: number) => {
        setSelectedPhoto(photos?.find((p) => id === p.id));
    };

    const removePhoto = (id: number) => {
        if (photos?.length && selectedJob?.uploads?.items.length) {
            const filteredPhotos = photos.filter((p) => p.id !== id);
            selectedJob.uploads.items = filteredPhotos;
            setPhotos(filteredPhotos);
            setSelectedPhoto(undefined);
        }
    };

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose}>
            <ModalHeader>
                <Title>Photo History</Title>
            </ModalHeader>
            <ModalContent>
                {selectedJob?.uploads?.items.length ? (
                    <div
                        className={css`
                            display: flex;

                            @media (max-width: 1000px) {
                                flex-direction: column-reverse;
                            }
                        `}
                        style={{ display: 'flex' }}>
                        <PhotoExplorer
                            photos={photos}
                            selectedPhoto={selectedPhoto}
                            selectPhoto={selectPhoto}
                        />
                        <PhotoInspector
                            selectedPhoto={selectedPhoto}
                            selectedJob={selectedJob}
                            removePhoto={removePhoto}
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            height: '30vw',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                        No photo history to show.
                    </div>
                )}
            </ModalContent>
            <ModalActions>
                <Button onClick={onClose}>Close</Button>
            </ModalActions>
        </Modal>
    );
});
